import React from 'react';
import { motion } from 'framer-motion';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { SideCart } from '../components/SideCart';
import { ChatBot } from '../components/ChatBot';
import { useCart } from '../context/CartContext';

const sections = [
  {
    title: 'Using this site',
    body: [
      'By using berleto.com, you agree to these terms. If you do not agree, please do not use the site or place an order.',
      'You agree to use the site only for lawful purposes and not to interfere with the security, operation, or availability of the store.',
    ],
  },
  {
    title: 'Products and information',
    body: [
      'Berleto products are dietary supplements. Product information on this site is provided for general wellness and educational purposes and is not medical advice.',
      'Statements on this site have not been evaluated by the Food and Drug Administration. Our products are not intended to diagnose, treat, cure, or prevent any disease. Consult a qualified healthcare professional before using supplements, especially if you are pregnant, nursing, have a medical condition, or take medication.',
    ],
  },
  {
    title: 'Orders and payment',
    body: [
      'When you place an order, you agree that all information you provide is accurate and complete. We may refuse, cancel, or limit orders where fraud, pricing errors, inventory issues, or misuse are suspected.',
      'Prices, discounts, product availability, and promotions may change without notice. Taxes and shipping details are calculated during checkout where applicable.',
    ],
  },
  {
    title: 'Shipping and returns',
    body: [
      'Shipping, delivery estimates, returns, and refund timing are governed by our Shipping & Returns page. Delivery dates are estimates and may vary due to carrier delays or operational conditions.',
      'If an order issue occurs, contact sales@berleto.com and include your order number so we can help quickly.',
    ],
  },
  {
    title: 'Subscriptions',
    body: [
      'If you purchase a subscription, you authorize recurring charges according to the subscription terms shown at checkout. You can cancel, pause, or adjust eligible subscriptions through your account or by contacting support.',
      'Subscription changes may need to be made before the next processing date to apply to the upcoming shipment.',
    ],
  },
  {
    title: 'Intellectual property',
    body: [
      'The Berleto name, logo, product imagery, copy, graphics, and site content are owned by Berleto or its licensors. You may not copy, reproduce, modify, distribute, or use our content for commercial purposes without written permission.',
    ],
  },
  {
    title: 'Limitation of liability',
    body: [
      'To the fullest extent permitted by law, Berleto is not liable for indirect, incidental, special, consequential, or punitive damages arising from your use of the site or products.',
      'Nothing in these terms limits rights that cannot be limited under applicable law.',
    ],
  },
  {
    title: 'Changes to these terms',
    body: [
      'We may update these terms from time to time. The updated version will be posted on this page with a revised date.',
    ],
  },
  {
    title: 'Contact',
    body: [
      'For questions about these terms, contact sales@berleto.com.',
    ],
  },
];

export function TermsPage() {
  const { isCartOpen, setIsCartOpen, cartItems, updateQuantity, removeItem, cartItemsCount } = useCart();

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#FAF9F6' }}>
      <Navigation onCartOpen={() => setIsCartOpen(true)} cartItemsCount={cartItemsCount} />

      <section className="pt-32 pb-16 border-b" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
        <div className="container mx-auto px-5 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="grid md:grid-cols-[1fr_280px] gap-10 md:items-end"
          >
            <div>
              <p className="text-xs font-medium tracking-[0.3em] uppercase mb-6" style={{ color: '#B8704D' }}>
                Terms
              </p>
              <h1 className="text-5xl md:text-7xl font-light leading-tight mb-6" style={{ color: '#1A2A1A' }}>
                Clear terms,<br />
                <span style={{ color: 'rgba(26,42,26,0.28)' }}>plain language.</span>
              </h1>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.55)', maxWidth: '48ch' }}>
                These terms explain the rules for using Berleto, placing orders, and interacting with our store.
              </p>
            </div>

            <div className="border-t pt-6" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
              <p className="text-xs tracking-[0.18em] uppercase mb-3" style={{ color: 'rgba(26,42,26,0.4)' }}>
                Last updated
              </p>
              <p className="text-2xl font-light" style={{ color: '#1A2A1A' }}>
                May 20, 2026
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-5 sm:px-6">
          <div className="grid md:grid-cols-[220px_1fr] gap-12 md:gap-16 items-start">
            <aside className="hidden md:block md:sticky md:top-32">
              <p className="text-xs font-medium tracking-[0.25em] uppercase mb-5" style={{ color: '#B8704D' }}>
                Agreement
              </p>
              <div className="border-t" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                {sections.map((section) => (
                  <a
                    key={section.title}
                    href={`#${section.title.toLowerCase().replaceAll(' ', '-')}`}
                    className="block border-b py-3 text-xs transition-colors"
                    style={{ borderColor: 'rgba(26,42,26,0.08)', color: 'rgba(26,42,26,0.48)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#B8704D'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(26,42,26,0.48)'; }}
                  >
                    {section.title}
                  </a>
                ))}
              </div>
            </aside>

            <div className="border-t" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
              {sections.map((section, index) => (
                <motion.section
                  key={section.title}
                  id={section.title.toLowerCase().replaceAll(' ', '-')}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: Math.min(index * 0.04, 0.2), ease: [0.22, 1, 0.36, 1] }}
                  className="grid md:grid-cols-[56px_1fr] gap-5 border-b py-8 md:py-10"
                  style={{ borderColor: 'rgba(26,42,26,0.08)' }}
                >
                  <span className="text-xs font-medium tracking-[0.18em]" style={{ color: '#B8704D' }}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-light mb-5" style={{ color: '#1A2A1A' }}>
                      {section.title}
                    </h2>
                    <div className="space-y-4">
                      {section.body.map((paragraph) => (
                        <p key={paragraph} className="text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.58)', maxWidth: '72ch' }}>
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                </motion.section>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <SideCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        updateQuantity={updateQuantity}
        removeItem={removeItem}
      />

      <ChatBot />
    </div>
  );
}
